import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import ImageUploader from "@/components/admin/ImageUploader";

export type LeadMagnetConfig = {
  enabled: boolean;
  title: string;
  description: string;
  cta_label: string;
  file_url: string;
  cover_image: string;
  success_message: string;
};

export type GoogleAdsConfig = {
  headline1: string;
  headline2: string;
  headline3: string;
  description1: string;
  description2: string;
  offer_text: string;
  final_url: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
};

export type ConsultantModeConfig = {
  enabled: boolean;
  dossier: string;
  sales_pitch: string;
  whatsapp_script: string;
  social_post: string;
  email_template: string;
  internal_notes: string;
};

export type Phase4Value = {
  lead_magnet: LeadMagnetConfig;
  google_ads: GoogleAdsConfig;
  consultant_mode: ConsultantModeConfig;
};

export const EMPTY_PHASE4: Phase4Value = {
  lead_magnet: {
    enabled: false, title: "", description: "", cta_label: "Baixar material completo",
    file_url: "", cover_image: "", success_message: "Obrigado! Enviamos o material para o seu WhatsApp.",
  },
  google_ads: {
    headline1: "", headline2: "", headline3: "",
    description1: "", description2: "", offer_text: "", final_url: "",
    utm_source: "google", utm_medium: "cpc", utm_campaign: "", utm_term: "", utm_content: "",
  },
  consultant_mode: {
    enabled: false, dossier: "", sales_pitch: "", whatsapp_script: "",
    social_post: "", email_template: "", internal_notes: "",
  },
};

type Props = { value: Phase4Value; onChange: (next: Phase4Value) => void };

const Phase4Form = ({ value, onChange }: Props) => {
  const lm = value.lead_magnet;
  const ga = value.google_ads;
  const cm = value.consultant_mode;

  const setLm = (patch: Partial<LeadMagnetConfig>) =>
    onChange({ ...value, lead_magnet: { ...lm, ...patch } });
  const setGa = (patch: Partial<GoogleAdsConfig>) =>
    onChange({ ...value, google_ads: { ...ga, ...patch } });
  const setCm = (patch: Partial<ConsultantModeConfig>) =>
    onChange({ ...value, consultant_mode: { ...cm, ...patch } });

  return (
    <details className="border border-border rounded-md">
      <summary className="cursor-pointer select-none px-3 py-2 font-body font-semibold text-sm text-foreground">
        🚀 Fase 4 — Lead Magnet, Google Ads e Modo Consultor
      </summary>

      <div className="p-3 space-y-6">
        {/* Lead Magnet */}
        <section className="space-y-3 border border-dashed border-border rounded-md p-3">
          <div className="flex items-center justify-between">
            <h4 className="font-heading font-semibold text-foreground">🎁 Lead Magnet</h4>
            <div className="flex items-center gap-2">
              <Switch checked={lm.enabled} onCheckedChange={(v) => setLm({ enabled: v })} />
              <span className="text-xs font-body text-muted-foreground">Ativar</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground font-body">
            Ofereça um material rico (PDF, tour, plantas, book) em troca do contato do lead.
          </p>
          <Input placeholder="Título (ex.: Book completo do apartamento)" value={lm.title}
            onChange={(e) => setLm({ title: e.target.value })} className="font-body" />
          <Textarea placeholder="Descrição do material" value={lm.description} rows={2}
            onChange={(e) => setLm({ description: e.target.value })} className="font-body" />
          <Input placeholder="Texto do botão (CTA)" value={lm.cta_label}
            onChange={(e) => setLm({ cta_label: e.target.value })} className="font-body" />
          <div>
            <Label className="text-xs font-body">URL do arquivo (PDF, drive, etc.)</Label>
            <Input placeholder="https://..." value={lm.file_url}
              onChange={(e) => setLm({ file_url: e.target.value })} className="font-body" />
          </div>
          <div>
            <Label className="text-xs font-body">Capa do material</Label>
            <ImageUploader images={lm.cover_image ? [lm.cover_image] : []}
              onChange={(imgs) => setLm({ cover_image: imgs[imgs.length - 1] || "" })} />
          </div>
          <Input placeholder="Mensagem de sucesso" value={lm.success_message}
            onChange={(e) => setLm({ success_message: e.target.value })} className="font-body" />
        </section>

        {/* Google Ads */}
        <section className="space-y-3 border border-dashed border-border rounded-md p-3">
          <h4 className="font-heading font-semibold text-foreground">📢 Google Ads (por imóvel)</h4>
          <p className="text-xs text-muted-foreground font-body">
            Materiais prontos para colar em campanhas Search do Google Ads.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Input placeholder="Headline 1 (30)" maxLength={30} value={ga.headline1}
              onChange={(e) => setGa({ headline1: e.target.value })} className="font-body" />
            <Input placeholder="Headline 2 (30)" maxLength={30} value={ga.headline2}
              onChange={(e) => setGa({ headline2: e.target.value })} className="font-body" />
            <Input placeholder="Headline 3 (30)" maxLength={30} value={ga.headline3}
              onChange={(e) => setGa({ headline3: e.target.value })} className="font-body" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <Textarea placeholder="Descrição 1 (90)" maxLength={90} rows={2}
              value={ga.description1} onChange={(e) => setGa({ description1: e.target.value })}
              className="font-body" />
            <Textarea placeholder="Descrição 2 (90)" maxLength={90} rows={2}
              value={ga.description2} onChange={(e) => setGa({ description2: e.target.value })}
              className="font-body" />
          </div>
          <Input placeholder="Oferta / gancho (ex.: Últimas unidades, vista Lagoa)" value={ga.offer_text}
            onChange={(e) => setGa({ offer_text: e.target.value })} className="font-body" />
          <Input placeholder="URL final (deixe vazio para usar o link do imóvel)" value={ga.final_url}
            onChange={(e) => setGa({ final_url: e.target.value })} className="font-body" />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Input placeholder="utm_source" value={ga.utm_source}
              onChange={(e) => setGa({ utm_source: e.target.value })} className="font-body" />
            <Input placeholder="utm_medium" value={ga.utm_medium}
              onChange={(e) => setGa({ utm_medium: e.target.value })} className="font-body" />
            <Input placeholder="utm_campaign" value={ga.utm_campaign}
              onChange={(e) => setGa({ utm_campaign: e.target.value })} className="font-body" />
            <Input placeholder="utm_term" value={ga.utm_term}
              onChange={(e) => setGa({ utm_term: e.target.value })} className="font-body" />
            <Input placeholder="utm_content" value={ga.utm_content}
              onChange={(e) => setGa({ utm_content: e.target.value })} className="font-body" />
          </div>
        </section>

        {/* Modo Consultor */}
        <section className="space-y-3 border border-dashed border-border rounded-md p-3">
          <div className="flex items-center justify-between">
            <h4 className="font-heading font-semibold text-foreground">🧠 Modo Consultor</h4>
            <div className="flex items-center gap-2">
              <Switch checked={cm.enabled} onCheckedChange={(v) => setCm({ enabled: v })} />
              <span className="text-xs font-body text-muted-foreground">Ativar</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground font-body">
            Materiais internos que o corretor acessa em <code>/imovel/…?consultor=1</code> e copia com 1 clique.
          </p>
          <Textarea placeholder="Dossiê do imóvel (resumo executivo)" rows={4} value={cm.dossier}
            onChange={(e) => setCm({ dossier: e.target.value })} className="font-body" />
          <Textarea placeholder="Pitch de venda (argumentos-chave)" rows={4} value={cm.sales_pitch}
            onChange={(e) => setCm({ sales_pitch: e.target.value })} className="font-body" />
          <Textarea placeholder="Roteiro de WhatsApp" rows={4} value={cm.whatsapp_script}
            onChange={(e) => setCm({ whatsapp_script: e.target.value })} className="font-body" />
          <Textarea placeholder="Post para redes sociais" rows={3} value={cm.social_post}
            onChange={(e) => setCm({ social_post: e.target.value })} className="font-body" />
          <Textarea placeholder="Template de e-mail" rows={4} value={cm.email_template}
            onChange={(e) => setCm({ email_template: e.target.value })} className="font-body" />
          <Textarea placeholder="Notas internas (não são exibidas)" rows={2} value={cm.internal_notes}
            onChange={(e) => setCm({ internal_notes: e.target.value })} className="font-body" />
        </section>
      </div>
    </details>
  );
};

export default Phase4Form;
